import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, OnChanges, SimpleChange } from '@angular/core';
import { AppState } from '../common/utils/AppState';

@Directive({
  selector: '[inumbervn]'
})
export class InumberVnDirective implements OnChanges {

  private decimalCounter = 0;
  private currentDecimalLength: number = 0;

  private navigationKeys = [
    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
    'Home', 'End', 'ArrowLeft', 'ArrowRight',
    'Clear', 'Copy', 'Paste'
  ];

  @Output() onInputChange = new EventEmitter<number>();
  @Input() value: number | null = null;
  @Input('inumbervn') config: any;
  @Input() maxLength: number = 20;
  inputElement: HTMLInputElement;

  constructor(public el: ElementRef, private _appState: AppState) {
    this.config = _appState.getConfigNumberDirective('vi');
    this.inputElement = el.nativeElement;
  }

  ngAfterViewInit(): void {
    this.config = this._appState.getConfigNumberDirective('vi');
    if (this.value !== null) {
      let valueStr = this.value.toString().replace('.', this.config.decimalSeparator);
      this.sanitizeInput(valueStr);
    }
  }

  ngOnChanges(changes: { [propName: string]: SimpleChange }) {
    if (changes['value'] && changes['value'].currentValue !== changes['value'].previousValue) {
      let newValue = changes['value'].currentValue;
      if (newValue !== null) {
        if (this.config.decimalLength !== undefined) {
          let n = this.config.decimalLength;
          newValue = Math.round(newValue * Math.pow(10, n)) / Math.pow(10, n);
        }
        this.sanitizeInput(newValue.toString().replace('.', this.config.decimalSeparator));
      } else {
        this.sanitizeInput('');
      }
    }
  }

  @HostListener('change')
  onChange(): void {
    let valueInput = this.inputElement.value;
    if (valueInput.includes(this.config.decimalSeparator)) {
      let parts = valueInput.split(this.config.decimalSeparator);
      if (parts[1].length <= 0) {
        this.decimalCounter = 0;
        valueInput = parts[0];
      }
    }
    let formattedValue = this.formatNumber(valueInput);
    this.inputElement.value = formattedValue;
    this.onInputChange.emit(this.parseNumber(formattedValue));
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    if (
      this.navigationKeys.includes(e.key) || // Allow: navigation keys (Backspace, Delete, etc.)
      (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) || // Allow: Ctrl + A, C, V, X
      (e.metaKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) || // Allow: Cmd + A, C, V, X (Mac)
      (this.config.decimal && e.key === this.config.decimalSeparator && this.decimalCounter < 1) // Allow: decimal separator
    ) {
      return; // Allow these key actions
    }

    // Prevent invalid inputs
    if (e.key === ' ' || isNaN(Number(e.key)) || this.invalidInput()) {
      e.preventDefault();
    }
  }

  private invalidInput(): boolean {
    const { selectionStart: start } = this.inputElement;
    let decimalIdx = this.inputElement.value.indexOf(this.config.decimalSeparator);
    if (decimalIdx > -1) {
      if (this.currentDecimalLength >= this.config.decimalLength && start! > decimalIdx) {
        return true;
      }
      let parts = this.inputElement.value.split(this.config.decimalSeparator);
      if (parts[0].length > this.maxLength) {
        return true;
      }
    } else {
      if (this.inputElement.value.length > this.maxLength) {
        return true;
      }
    }
    return false;
  }

  @HostListener('keyup', ['$event'])
  onKeyUp(e: KeyboardEvent) {
    let valueInput = this.inputElement.value;
    if (!this.config.decimal) return;

    let parts = valueInput.split(this.config.decimalSeparator);
    this.decimalCounter = parts.length - 1;
    if (parts.length >= 2) {
      let decimalValue = parts[1].substring(0, this.config.decimalLength);
      valueInput = parts[0] + this.config.decimalSeparator + decimalValue;
      this.currentDecimalLength = parts[1].length;
    }
    this.sanitizeInput(valueInput);
  }

  private sanitizeInput(valueInput: string): void {
    let newValue = this.formatNumber(valueInput);
    this.inputElement.value = newValue;
    setTimeout(() => {
      this.inputElement.selectionStart = this.inputElement.selectionEnd = newValue.length;
    });
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    // Get clipboard data
    let clipboardData = event.clipboardData?.getData('text') || '';

    // Remove non-numeric characters except decimal and group separators
    let sanitizedValue = clipboardData.replace(/[^0-9.,-]/g, '');

    // Convert to expected format
    let formattedValue = this.formatNumber(sanitizedValue);

    // Set value in input field
    this.inputElement.value = formattedValue;

    // Emit parsed number value
    this.onInputChange.emit(this.parseNumber(formattedValue));
  }

  private formatNumber(valueInput: string): string {
    if (!valueInput.trim()) return '';

    let decimalValue = '';
    let decimalSeparator = '';
    let value = valueInput;

    if (value.includes(this.config.decimalSeparator)) {
      let parts = value.split(this.config.decimalSeparator);
      value = parts[0];
      decimalValue = parts[1]?.substring(0, this.config.decimalLength) ?? '';
      decimalSeparator = this.config.decimalSeparator;
    }

    value = this.parseNumber(value).toString();

    if (isNaN(Number(value))) {
      return value + decimalSeparator + decimalValue;
    }

    let sign = Number(value) >= 0 ? '' : '-';
    let absValue = Math.abs(Number(value)).toFixed(0);
    let formattedValue = absValue.replace(/\B(?=(\d{3})+(?!\d))/g, this.config.groupSeparator);

    return sign + formattedValue + decimalSeparator + decimalValue;
  }

  private parseNumber(value: string): number {
    if (!value.trim()) return 0;
    return Number(value.split(this.config.groupSeparator).join('').replace(this.config.decimalSeparator, '.'));
  }
}
